/**
 * ErrorBoundary — catches uncaught render errors at three levels (root / app / route).
 *
 * NOTE: componentDidCatch does NOT call Sentry.captureException.
 * ReactDOM.createRoot's onCaughtError already wires Sentry.reactErrorHandler()
 * (see frontend/src/index.tsx). Adding captureException here would produce two
 * Sentry events per crash. If onCaughtError is ever removed from index.tsx,
 * add captureException HERE — do not add it in individual componentDidCatch calls.
 */

import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { Component, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IS_LOCAL } from '../config/env';
import { attemptChunkReload } from '../services/pwa/chunk-error-reloader';

type ErrorBoundaryLevel = 'root' | 'app' | 'route';

interface ErrorBoundaryProps {
  children: ReactNode;
  level?: ErrorBoundaryLevel;
  fallback?: (state: { error: Error; reset: () => void }) => ReactElement;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryFallbackProps {
  error: Error;
  level: ErrorBoundaryLevel;
  onReload: () => void;
}

function ErrorBoundaryFallback({ error, onReload }: ErrorBoundaryFallbackProps) {
  // useTranslation must be called unconditionally (rules of hooks).
  // When i18n itself failed, t() returns the key unchanged — tr() detects that
  // and falls back to the hardcoded IT strings below.
  const { t } = useTranslation('common');

  const hardcoded = {
    title: 'Qualcosa è andato storto',
    subtitle: 'Si è verificato un errore imprevisto. Ricarica la pagina per continuare.',
    reloadButton: 'Ricarica',
    detailsLabel: 'Dettagli tecnici',
  };

  function tr(key: string, fallback: string): string {
    const result = t(`errorBoundary.${key}`);
    // i18next returns the key unchanged when translation is missing
    return result === `errorBoundary.${key}` ? fallback : result;
  }

  const title = tr('title', hardcoded.title);
  const subtitle = tr('subtitle', hardcoded.subtitle);
  const reloadButton = tr('reloadButton', hardcoded.reloadButton);
  const detailsLabel = tr('detailsLabel', hardcoded.detailsLabel);

  const stackText = error.stack ? error.stack.slice(0, 4000) : '';

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 9999,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 4,
      }}
    >
      <ErrorOutlinedIcon sx={{ fontSize: 64, color: '#0F172A' }} />
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
        }}
      >
        {subtitle}
      </Typography>
      <Button variant="contained" onClick={onReload} sx={{ mt: 1 }}>
        {reloadButton}
      </Button>
      {IS_LOCAL && (
        <Accordion sx={{ mt: 2, width: '100%', maxWidth: 600 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
              }}
            >
              {detailsLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              component="pre"
              variant="caption"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                fontFamily: 'monospace',
              }}
            >
              {error.name}: {error.message}
              {stackText ? `\n\n${stackText}` : ''}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (attemptChunkReload(error)) {
      return;
    }
    // Log locally for visibility. Sentry reporting is handled by
    // ReactDOM.createRoot's onCaughtError — see note at top of file.
    console.error('[ErrorBoundary]', this.props.level ?? 'route', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback, level = 'route' } = this.props;

    if (hasError && error) {
      if (fallback) {
        return fallback({ error, reset: this.reset });
      }
      return (
        <ErrorBoundaryFallback
          error={error}
          level={level}
          onReload={() => window.location.reload()}
        />
      );
    }

    return children;
  }
}
